import { create } from 'zustand';
import { supabase, cloudConfigured } from '../../lib/supabase.js';
import { onUserChanged } from '../../sync/controller.js';
import { queryClient } from '../../api/queryClient.js';
import { queryKeys } from '../../api/keys.js';

export const useAuth = create(() => ({ ready: false, user: null, session: null, recovery: false, linkError: null }));

const OTP_TYPES = new Set(['signup', 'invite', 'magiclink', 'recovery', 'email_change', 'email']);

export function needsReauth(error) {
  const msg = `${error?.code ?? ''} ${error?.message ?? ''}`.toLowerCase();
  return msg.includes('reauth') || msg.includes('nonce');
}

function readHash() {
  const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const description = hash.get('error_description');
  return {
    type: hash.get('type'),
    error: description ? description.replace(/\+/g, ' ') : null,
  };
}

let initialized = false;

export async function initAuth() {
  if (initialized) return;
  initialized = true;
  if (!cloudConfigured) {
    useAuth.setState({ ready: true });
    onUserChanged(null);
    return;
  }
  const link = readHash();
  if (link.type === 'recovery') useAuth.setState({ recovery: true });
  if (link.error) useAuth.setState({ linkError: link.error });
  const apply = (session) => {
    useAuth.setState({ ready: true, session, user: session?.user ?? null });
    queryClient.setQueryData(queryKeys.session, session ?? null);
    onUserChanged(session?.user ?? null);
  };
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY') useAuth.setState({ recovery: true });
    // Defer: supabase-js recommends not awaiting other calls inside this callback.
    setTimeout(() => apply(session), 0);
  });
  const { data } = await supabase.auth.getSession();
  apply(data.session);
}

export async function signIn(email, password) {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function signUp(email, password) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: window.location.origin },
  });
  if (error) throw error;
  return { needsConfirmation: !data.session };
}

export async function resetPassword(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth?mode=update`,
  });
  if (error) throw error;
}

export async function sendMagicLink(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin, shouldCreateUser: false },
  });
  if (error) throw error;
}

export async function verifyEmailCode(email, token, type) {
  const types = type === 'email' || type === 'magiclink' ? ['email', 'magiclink'] : [type];
  if (types.some((item) => !OTP_TYPES.has(item))) throw new Error('Choose what this code is for.');
  let last = null;
  for (const item of types) {
    const { data, error } = await supabase.auth.verifyOtp({ email, token, type: item });
    if (!error) {
      if (item === 'recovery') useAuth.setState({ recovery: true });
      return data;
    }
    last = error;
  }
  throw last;
}

export async function resendAuthEmail(email, purpose) {
  if (purpose === 'email' || purpose === 'magiclink') {
    await sendMagicLink(email);
    return;
  }
  if (purpose === 'signup' || purpose === 'email_change') {
    const { error } = await supabase.auth.resend({
      type: purpose,
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
    return;
  }
  if (purpose === 'recovery') {
    await resetPassword(email);
    return;
  }
  throw new Error('Send a new email from the sign-in screen.');
}

export async function changeEmail(email, nonce) {
  const { data, error } = await supabase.auth.updateUser(nonce ? { email, nonce } : { email });
  if (error) throw error;
  return data;
}

export async function requestReauth() {
  const { error } = await supabase.auth.reauthenticate();
  if (error) throw error;
}

export async function updatePassword(password, nonce) {
  const { error } = await supabase.auth.updateUser(nonce ? { password, nonce } : { password });
  if (error) throw error;
  useAuth.setState({ recovery: false });
}

export async function signOut() {
  await supabase.auth.signOut({ scope: 'local' });
}
